import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../Context/BrandingContext';
import { checkEmail } from '../Services/services';
import {
  isEmailSubsystemNotice,
  isRejectedEngagementStatus,
  SUSPENDED_ACCOUNT_MESSAGE,
} from '../constants/authMessages';

function Email({ onEmailSubmit, onCancel, variant = 'page' }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const { loading, error: authError } = useSelector((state) => state.auth);
  const { branding } = useBranding();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setChecking(true);

    try {
      // Call the new auth/check API
      const response = await checkEmail(email.trim());
      const data = response.data;

      if (data.success) {
        if (isRejectedEngagementStatus(data.engagementStatus)) {
          setError(data.statusMessage || SUSPENDED_ACCOUNT_MESSAGE);
          return;
        }

        // User found - pass result to parent
        if (onEmailSubmit) {
          onEmailSubmit(email.trim(), {
            found: true,
            engagementStatus: data.engagementStatus,
            roles: data.roles,
            statusMessage: data.statusMessage,
          });
        }
      } else {
        // Handle error response
        setError(data.message || 'Verification failed');
      }
    } catch (err) {
      // Check if it's a 404 (not found) - this means user needs to register
      if (err.response?.status === 404) {
        if (onEmailSubmit) {
          onEmailSubmit(email.trim(), { status: 404, notFound: true });
        }
      } else if (err.response?.status === 403) {
        setError(
          err.response?.data?.message || SUSPENDED_ACCOUNT_MESSAGE,
        );
      } else {
        // Other errors
        const errorMessage = err.response?.data?.message || 
                            err.response?.data?.error || 
                            'Verification failed. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setChecking(false);
    }
  };

  const isLoading = loading || checking;

  const displayError = error || authError;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    // Always return to landing page, ignoring any redirect param
    navigate('/', { replace: true });
  };

  const content = (
    <>
      <div style={styles.modalHeader}>
        <h1 style={styles.modalTitle}>Welcome</h1>
        <p style={styles.modalSubtitle}>Enter your email to sign in or create your new account</p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            style={styles.modalInput}
            disabled={isLoading}
          />
        </div>

        {displayError && (
          <div style={isEmailSubsystemNotice(displayError) ? styles.modalInfo : styles.modalError}>
            {displayError}
          </div>
        )}

        <button type="submit" style={{ ...styles.modalPrimaryButton, backgroundColor: branding.primaryColor }} disabled={isLoading}>
          {isLoading ? 'Checking...' : 'Continue'}
        </button>

        <button type="button" onClick={handleCancel} style={styles.modalSecondaryButton} disabled={isLoading}>
          Cancel
        </button>
      </form>
    </>
  );

  if (variant === 'modal') {
    return <div>{content}</div>;
  }

  // Page (legacy) layout wrapper
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ ...styles.header, borderBottom: `2px solid ${branding.primaryColor}` }}>
          <p style={{ ...styles.companyName, color: branding.primaryColor }}>{branding.companyName}</p>
          <h1 style={{ ...styles.title, color: branding.primaryColor }}>Customer Portal</h1>
          <p style={styles.subtitle}>Sign in to access your account</p>
        </div>
        <p style={styles.instruction}>Enter your email to receive a verification code</p>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            required
            style={styles.input}
            disabled={isLoading}
          />
          {displayError && (
            <div style={isEmailSubsystemNotice(displayError) ? styles.infoNotice : styles.error}>
              {displayError}
            </div>
          )}
          <button type="submit" style={{ ...styles.button, backgroundColor: branding.primaryColor }} disabled={isLoading}>
            {isLoading ? 'Checking...' : 'Continue'}
          </button>
          <button type="button" onClick={handleCancel} style={{ ...styles.cancelButton, color: branding.primaryColor, borderColor: branding.primaryColor }} disabled={isLoading}>
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '400px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '2px solid #0066cc',
  },
  companyName: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700',
    color: '#0066cc',
  },
  subtitle: {
    margin: '0',
    color: '#666',
    fontSize: '16px',
  },
  instruction: {
    margin: '0 0 16px 0',
    color: '#333',
    fontSize: '14px',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#0066cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cancelButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#0066cc',
    border: '1px solid #0066cc',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '12px',
  },
  error: {
    color: '#d32f2f',
    fontSize: '14px',
    marginBottom: '16px',
  },
  infoNotice: {
    color: '#1e40af',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '10px 12px',
    backgroundColor: '#eff6ff',
    borderRadius: '4px',
    border: '1px solid #bfdbfe',
    lineHeight: 1.45,
  },

  // Modal variant (Figma Make-inspired)
  modalHeader: {
    marginBottom: 20,
  },
  modalTitle: {
    margin: '0 0 6px 0',
    fontSize: 28,
    fontWeight: 600,
    color: 'var(--foreground)',
  },
  modalSubtitle: {
    margin: 0,
    fontSize: 14,
    color: 'var(--muted-foreground)',
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--foreground)',
  },
  modalInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    backgroundColor: 'var(--input-background)',
    color: 'var(--foreground)',
    fontSize: 14,
    boxSizing: 'border-box',
  },
  modalPrimaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: 'none',
    color: 'var(--primary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalSecondaryButton: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    backgroundColor: 'var(--secondary)',
    color: 'var(--secondary-foreground)',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  modalError: {
    color: 'var(--destructive)',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(220, 38, 38, 0.08)',
    border: '1px solid rgba(220, 38, 38, 0.25)',
  },
  modalInfo: {
    color: '#1e3a8a',
    fontSize: 13,
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.35)',
    lineHeight: 1.45,
  },
};

export default Email;

